import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import AddToEHR from "@/components/AddToEHR";
import {
  UserPlus,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  Building2,
  Stethoscope,
  Home,
  Phone,
  Mail,
  Calendar,
  FileText,
  TrendingUp,
  RefreshCw,
  ChevronRight,
  User,
  Activity,
  ClipboardList
} from "lucide-react";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  under_review: { label: "Under Review", color: "bg-blue-100 text-blue-800", icon: Eye },
  accepted: { label: "Accepted", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  admitted: { label: "Admitted", color: "bg-teal-100 text-teal-800", icon: Home },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800", icon: XCircle },
  waitlisted: { label: "Waitlisted", color: "bg-purple-100 text-purple-800", icon: Clock },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-800", icon: XCircle }
};

const PRIORITY_CONFIG = {
  urgent: { label: "Urgent", color: "bg-red-500 text-white" },
  high: { label: "High", color: "bg-orange-500 text-white" },
  normal: { label: "Normal", color: "bg-blue-500 text-white" },
  low: { label: "Low", color: "bg-gray-500 text-white" }
};

const SOURCE_CONFIG = {
  hospital: { label: "Hospital", icon: Building2 },
  physician: { label: "Physician", icon: Stethoscope },
  facility: { label: "Facility", icon: Home },
  family: { label: "Family", icon: Users },
  self: { label: "Self", icon: User },
  other: { label: "Other", icon: FileText }
};

export default function ReferralDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [totalReferrals, setTotalReferrals] = useState(0);
  const [assignableStaff, setAssignableStaff] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  
  // Dialogs
  const [showNewReferral, setShowNewReferral] = useState(false);
  const [showReferralDetail, setShowReferralDetail] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  
  // New referral form
  const [newReferral, setNewReferral] = useState({
    patient: { name: "", date_of_birth: "", phone: "", email: "", address: "", primary_diagnosis: "", prognosis: "", physician_name: "", physician_phone: "" },
    caregiver: { name: "", relationship: "", phone: "", email: "" },
    source: "other",
    source_organization: "",
    source_contact_name: "",
    source_contact_phone: "",
    priority: "normal",
    reason_for_referral: "",
    requested_services: [],
    special_needs: "",
    notes: ""
  });
  
  const [submitting, setSubmitting] = useState(false);

  const allowedRoles = ["admin", "administrator", "staff", "nurse", "social_worker", "facility", "hospital", "physician"];

  const loadDashboard = useCallback(async () => {
    try {
      const [statsRes, staffRes] = await Promise.all([
        api.get("/referrals/dashboard"),
        api.get("/referrals/staff/assignable")
      ]);
      setDashboardStats(statsRes.data);
      setAssignableStaff(staffRes.data.staff || []);
    } catch (error) {
      console.error("Failed to load dashboard:", error);
    }
  }, []);

  const loadReferrals = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (priorityFilter !== "all") params.append("priority", priorityFilter);
      if (sourceFilter !== "all") params.append("source", sourceFilter);
      if (searchTerm) params.append("search", searchTerm);
      
      const response = await api.get(`/referrals/list?${params.toString()}`);
      setReferrals(response.data.referrals || []);
      setTotalReferrals(response.data.total || 0);
    } catch (error) {
      console.error("Failed to load referrals:", error);
    }
  }, [statusFilter, priorityFilter, sourceFilter, searchTerm]);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || !allowedRoles.includes(user?.role?.toLowerCase())) {
      navigate("/", { replace: true });
      return;
    }
    
    const init = async () => {
      setLoading(true);
      await Promise.all([loadDashboard(), loadReferrals()]);
      setLoading(false);
    };
    
    init();
  }, [user, navigate, authLoading, loadDashboard, loadReferrals]);

  useEffect(() => {
    if (!loading) {
      loadReferrals();
    }
  }, [statusFilter, priorityFilter, sourceFilter, searchTerm]);

  const handleCreateReferral = async () => {
    if (!newReferral.patient.name) {
      toast.error("Patient name is required");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post("/referrals/create", newReferral);
      toast.success("Referral created successfully");
      setShowNewReferral(false);
      setNewReferral({
        patient: { name: "", date_of_birth: "", phone: "", email: "", address: "", primary_diagnosis: "", prognosis: "", physician_name: "", physician_phone: "" },
        caregiver: { name: "", relationship: "", phone: "", email: "" },
        source: "other",
        source_organization: "",
        source_contact_name: "",
        source_contact_phone: "",
        priority: "normal",
        reason_for_referral: "",
        requested_services: [],
        special_needs: "",
        notes: ""
      });
      loadDashboard();
      loadReferrals();
    } catch (error) {
      toast.error("Failed to create referral");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (referralId, newStatus, notes = "") => {
    try {
      await api.put(`/referrals/${referralId}`, { status: newStatus, rejection_reason: notes });
      toast.success(`Referral ${newStatus}`);
      loadDashboard();
      loadReferrals();
      setShowStatusUpdate(false);
      setSelectedReferral(null);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleAssign = async (referralId, staffId) => {
    try {
      await api.put(`/referrals/${referralId}`, { assigned_to: staffId });
      toast.success("Referral assigned");
      loadReferrals();
    } catch (error) {
      toast.error("Failed to assign referral");
    }
  };

  const viewReferralDetail = async (referralId) => {
    try {
      const response = await api.get(`/referrals/${referralId}`);
      setSelectedReferral(response.data);
      setShowReferralDetail(true);
    } catch (error) {
      toast.error("Failed to load referral details");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-teal-600 mb-4" />
          <p className="text-muted-foreground">Loading Referral Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/20 rounded-lg">
                  <ClipboardList className="h-6 w-6" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold">Referral Dashboard</h1>
              </div>
              <p className="text-teal-100">Manage patient referrals and admissions</p>
            </div>
            <div className="flex items-center gap-3">
              <AddToEHR 
                buttonVariant="default"
                buttonSize="default"
                buttonText="Add to EHR"
                className="bg-white/10 text-white hover:bg-white/20 border border-white/20"
              />
              <Button 
                onClick={() => setShowNewReferral(true)}
                className="bg-white text-teal-600 hover:bg-teal-50"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Referral
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        {dashboardStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <Card 
              className="bg-white/80 backdrop-blur border-0 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => { setStatusFilter('all'); setPriorityFilter('all'); }}
              data-testid="stat-total-referrals"
            >
              <CardContent className="p-4 text-center">
                <ClipboardList className="h-8 w-8 mx-auto text-teal-600 mb-2" />
                <p className="text-2xl font-bold text-teal-700">{dashboardStats.stats.total}</p>
                <p className="text-xs text-muted-foreground">Total Referrals</p>
                <p className="text-xs text-teal-500 mt-1">View all →</p>
              </CardContent>
            </Card>
            
            <Card 
              className="bg-white/80 backdrop-blur border-0 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => { setStatusFilter('pending'); setPriorityFilter('all'); }}
              data-testid="stat-pending"
            >
              <CardContent className="p-4 text-center">
                <Clock className="h-8 w-8 mx-auto text-yellow-600 mb-2" />
                <p className="text-2xl font-bold text-yellow-700">{dashboardStats.stats.pending}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="text-xs text-yellow-500 mt-1">View list →</p>
              </CardContent>
            </Card>
            
            <Card 
              className="bg-white/80 backdrop-blur border-0 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => { setStatusFilter('under_review'); setPriorityFilter('all'); }}
              data-testid="stat-under-review"
            >
              <CardContent className="p-4 text-center">
                <Eye className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                <p className="text-2xl font-bold text-blue-700">{dashboardStats.stats.under_review}</p>
                <p className="text-xs text-muted-foreground">Under Review</p>
                <p className="text-xs text-blue-500 mt-1">View list →</p>
              </CardContent>
            </Card>
            
            <Card 
              className="bg-white/80 backdrop-blur border-0 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => { setStatusFilter('accepted'); setPriorityFilter('all'); }}
              data-testid="stat-accepted"
            >
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="h-8 w-8 mx-auto text-green-600 mb-2" />
                <p className="text-2xl font-bold text-green-700">{dashboardStats.stats.accepted}</p>
                <p className="text-xs text-muted-foreground">Accepted</p>
                <p className="text-xs text-green-500 mt-1">View list →</p>
              </CardContent>
            </Card>
            
            <Card 
              className="bg-white/80 backdrop-blur border-0 shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => { setStatusFilter('admitted'); setPriorityFilter('all'); }}
              data-testid="stat-admitted"
            >
              <CardContent className="p-4 text-center">
                <Home className="h-8 w-8 mx-auto text-teal-600 mb-2" />
                <p className="text-2xl font-bold text-teal-700">{dashboardStats.stats.admitted}</p>
                <p className="text-xs text-muted-foreground">Admitted</p>
                <p className="text-xs text-teal-500 mt-1">View list →</p>
              </CardContent>
            </Card>
            
            <Card 
              className="bg-white/80 backdrop-blur border-0 shadow-sm cursor-pointer hover:shadow-lg transition-shadow border-2 border-red-200"
              onClick={() => { setPriorityFilter('urgent'); setStatusFilter('pending'); }}
              data-testid="stat-urgent-pending"
            >
              <CardContent className="p-4 text-center">
                <AlertTriangle className="h-8 w-8 mx-auto text-red-600 mb-2" />
                <p className="text-2xl font-bold text-red-700">{dashboardStats.stats.urgent_pending}</p>
                <p className="text-xs text-muted-foreground">Urgent Pending</p>
                <p className="text-xs text-red-500 mt-1">View urgent →</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Urgent Alert */}
        {dashboardStats?.stats.urgent_pending > 0 && (
          <Alert className="mb-6 bg-red-50 border-red-200">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <strong>{dashboardStats.stats.urgent_pending} urgent referral(s)</strong> require immediate attention!
            </AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search patient name or source..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="admitted">Admitted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="waitlisted">Waitlisted</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="hospital">Hospital</SelectItem>
                  <SelectItem value="physician">Physician</SelectItem>
                  <SelectItem value="facility">Facility</SelectItem>
                  <SelectItem value="family">Family</SelectItem>
                  <SelectItem value="self">Self</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" onClick={() => { loadDashboard(); loadReferrals(); }}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Referral List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Referrals ({totalReferrals})</CardTitle>
                <CardDescription>Click on a referral to view details and take action</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {referrals.map((referral) => {
                const statusConfig = STATUS_CONFIG[referral.status] || STATUS_CONFIG.pending;
                const priorityConfig = PRIORITY_CONFIG[referral.priority] || PRIORITY_CONFIG.normal;
                const sourceConfig = SOURCE_CONFIG[referral.source] || SOURCE_CONFIG.other;
                const SourceIcon = sourceConfig.icon;
                
                return (
                  <div 
                    key={referral.id}
                    className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => viewReferralDetail(referral.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center">
                          <User className="h-6 w-6 text-teal-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-lg">{referral.patient?.name}</p>
                            <Badge className={`${priorityConfig.color} text-xs`}>
                              {priorityConfig.label}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <SourceIcon className="h-3 w-3" />
                              {referral.source_organization || sourceConfig.label}
                            </span>
                            <span>•</span>
                            <span>{referral.patient?.primary_diagnosis || "No diagnosis"}</span>
                            <span>•</span>
                            <span>{new Date(referral.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {referral.assigned_to_name && (
                          <div className="text-right text-sm">
                            <p className="text-muted-foreground">Assigned to</p>
                            <p className="font-medium">{referral.assigned_to_name}</p>
                          </div>
                        )}
                        
                        <Badge className={`${statusConfig.color} border-0`}>
                          {statusConfig.label}
                        </Badge>
                        
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {referrals.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No referrals found</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowNewReferral(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Referral
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* New Referral Dialog */}
      <Dialog open={showNewReferral} onOpenChange={setShowNewReferral}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Patient Referral</DialogTitle>
            <DialogDescription>Enter patient and referral information</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Patient Info */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <User className="h-4 w-4" /> Patient Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Patient Name *</Label>
                  <Input
                    value={newReferral.patient.name}
                    onChange={(e) => setNewReferral({...newReferral, patient: {...newReferral.patient, name: e.target.value}})}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={newReferral.patient.date_of_birth}
                    onChange={(e) => setNewReferral({...newReferral, patient: {...newReferral.patient, date_of_birth: e.target.value}})}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newReferral.patient.phone}
                    onChange={(e) => setNewReferral({...newReferral, patient: {...newReferral.patient, phone: e.target.value}})}
                    placeholder="555-123-4567"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Primary Diagnosis</Label>
                  <Input
                    value={newReferral.patient.primary_diagnosis}
                    onChange={(e) => setNewReferral({...newReferral, patient: {...newReferral.patient, primary_diagnosis: e.target.value}})}
                    placeholder="e.g., Congestive Heart Failure"
                  />
                </div>
                <div>
                  <Label>Prognosis</Label>
                  <Input
                    value={newReferral.patient.prognosis}
                    onChange={(e) => setNewReferral({...newReferral, patient: {...newReferral.patient, prognosis: e.target.value}})}
                    placeholder="e.g., 6 months or less"
                  />
                </div>
                <div>
                  <Label>Referring Physician</Label>
                  <Input
                    value={newReferral.patient.physician_name}
                    onChange={(e) => setNewReferral({...newReferral, patient: {...newReferral.patient, physician_name: e.target.value}})}
                    placeholder="Dr. Smith"
                  />
                </div>
              </div>
            </div>

            {/* Source Info */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Referral Source
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Source Type</Label>
                  <Select value={newReferral.source} onValueChange={(v) => setNewReferral({...newReferral, source: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hospital">Hospital</SelectItem>
                      <SelectItem value="physician">Physician</SelectItem>
                      <SelectItem value="facility">Facility</SelectItem>
                      <SelectItem value="family">Family</SelectItem>
                      <SelectItem value="self">Self</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={newReferral.priority} onValueChange={(v) => setNewReferral({...newReferral, priority: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Organization Name</Label>
                  <Input
                    value={newReferral.source_organization}
                    onChange={(e) => setNewReferral({...newReferral, source_organization: e.target.value})}
                    placeholder="e.g., General Hospital"
                  />
                </div>
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={newReferral.source_contact_name}
                    onChange={(e) => setNewReferral({...newReferral, source_contact_name: e.target.value})}
                    placeholder="Contact person"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={newReferral.source_contact_phone}
                    onChange={(e) => setNewReferral({...newReferral, source_contact_phone: e.target.value})}
                    placeholder="555-123-4567"
                  />
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" /> Additional Information
              </h3>
              <div className="space-y-4">
                <div>
                  <Label>Reason for Referral</Label>
                  <Textarea
                    value={newReferral.reason_for_referral}
                    onChange={(e) => setNewReferral({...newReferral, reason_for_referral: e.target.value})}
                    placeholder="Brief description of why hospice care is needed"
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Special Needs / Notes</Label>
                  <Textarea
                    value={newReferral.special_needs}
                    onChange={(e) => setNewReferral({...newReferral, special_needs: e.target.value})}
                    placeholder="Any special requirements or additional notes"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewReferral(false)}>Cancel</Button>
            <Button onClick={handleCreateReferral} disabled={submitting}>
              {submitting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Referral
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Referral Detail Dialog */}
      <Dialog open={showReferralDetail} onOpenChange={setShowReferralDetail}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedReferral && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedReferral.patient?.name}</DialogTitle>
                    <DialogDescription>
                      Referral ID: {selectedReferral.id?.slice(0, 8)}... • Created {new Date(selectedReferral.created_at).toLocaleDateString()}
                    </DialogDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={PRIORITY_CONFIG[selectedReferral.priority]?.color}>
                      {PRIORITY_CONFIG[selectedReferral.priority]?.label}
                    </Badge>
                    <Badge className={STATUS_CONFIG[selectedReferral.status]?.color}>
                      {STATUS_CONFIG[selectedReferral.status]?.label}
                    </Badge>
                  </div>
                </div>
              </DialogHeader>

              <div className="grid md:grid-cols-2 gap-6 py-4">
                {/* Patient Details */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <User className="h-4 w-4" /> Patient Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div><span className="text-muted-foreground">DOB:</span> {selectedReferral.patient?.date_of_birth || "N/A"}</div>
                    <div><span className="text-muted-foreground">Phone:</span> {selectedReferral.patient?.phone || "N/A"}</div>
                    <div><span className="text-muted-foreground">Diagnosis:</span> {selectedReferral.patient?.primary_diagnosis || "N/A"}</div>
                    <div><span className="text-muted-foreground">Prognosis:</span> {selectedReferral.patient?.prognosis || "N/A"}</div>
                    <div><span className="text-muted-foreground">Physician:</span> {selectedReferral.patient?.physician_name || "N/A"}</div>
                  </CardContent>
                </Card>

                {/* Source Details */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Building2 className="h-4 w-4" /> Referral Source
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div><span className="text-muted-foreground">Source:</span> {SOURCE_CONFIG[selectedReferral.source]?.label}</div>
                    <div><span className="text-muted-foreground">Organization:</span> {selectedReferral.source_organization || "N/A"}</div>
                    <div><span className="text-muted-foreground">Contact:</span> {selectedReferral.source_contact_name || "N/A"}</div>
                    <div><span className="text-muted-foreground">Phone:</span> {selectedReferral.source_contact_phone || "N/A"}</div>
                    <div><span className="text-muted-foreground">Created by:</span> {selectedReferral.created_by_name}</div>
                  </CardContent>
                </Card>

                {/* Reason & Notes */}
                <Card className="md:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Reason for Referral</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{selectedReferral.reason_for_referral || "No reason provided"}</p>
                    {selectedReferral.special_needs && (
                      <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                        <p className="text-sm font-medium text-amber-800">Special Needs:</p>
                        <p className="text-sm text-amber-700">{selectedReferral.special_needs}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Status History */}
                <Card className="md:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Activity className="h-4 w-4" /> Status History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedReferral.status_history?.map((entry, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-sm">
                          <div className={`w-2 h-2 rounded-full ${STATUS_CONFIG[entry.status]?.color.split(' ')[0]}`}></div>
                          <span className="font-medium capitalize">{entry.status.replace('_', ' ')}</span>
                          <span className="text-muted-foreground">by {entry.changed_by_name}</span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Assign to:</Label>
                  <Select 
                    value={selectedReferral.assigned_to || ""} 
                    onValueChange={(v) => handleAssign(selectedReferral.id, v)}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select staff" />
                    </SelectTrigger>
                    <SelectContent>
                      {assignableStaff.map(staff => (
                        <SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  {selectedReferral.status === "pending" && (
                    <>
                      <Button variant="outline" onClick={() => handleStatusUpdate(selectedReferral.id, "under_review")}>
                        <Eye className="h-4 w-4 mr-2" /> Start Review
                      </Button>
                      <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleStatusUpdate(selectedReferral.id, "accepted")}>
                        <CheckCircle2 className="h-4 w-4 mr-2" /> Accept
                      </Button>
                    </>
                  )}
                  {selectedReferral.status === "under_review" && (
                    <>
                      <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleStatusUpdate(selectedReferral.id, "accepted")}>
                        <CheckCircle2 className="h-4 w-4 mr-2" /> Accept
                      </Button>
                      <Button variant="destructive" onClick={() => handleStatusUpdate(selectedReferral.id, "rejected", "Does not meet criteria")}>
                        <XCircle className="h-4 w-4 mr-2" /> Reject
                      </Button>
                    </>
                  )}
                  {selectedReferral.status === "accepted" && (
                    <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => handleStatusUpdate(selectedReferral.id, "admitted")}>
                      <Home className="h-4 w-4 mr-2" /> Mark Admitted
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
